import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { EnumSection, ITrophyTooltip } from '../../interfaces/clientInterfaces';
import { Link, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IStore } from '../../interfaces';
import { useClickOutside } from '../../utils/useClickOutside';
import * as actions from '../../actions';
import { useAppDispatch } from '../../utils/modules';

interface ITrophyTooltipProps {
	trophyTooltip: ITrophyTooltip;
}

interface ITrophyTooltipContainer {
	x: number;
	y: number;
}
const TrophyTooltipContainer = styled.div<ITrophyTooltipContainer>`
	position: absolute;
	left: ${props => props.x}px;
	top: ${props => props.y}px;
	font-size: 12px;
	width: 275px;
	min-height: 60px;
	border-radius: 4px;
	border: 1px  solid #F89504;
	color: white;
	text-align: center;
	text-shadow: 2px 2px 2px gold;
	background-color: black;
	padding: 8px;
	opacity: 0.8;
	user-select: none;
	z-index: 1111114;
	p {
		margin: 5px 0 0 0;
	}
`;

interface IDescriptionContainer {
	isMobileViewActive: boolean;
}
const DescriptionContainer = styled.div<IDescriptionContainer>`
	min-height: 50px;
	max-height: ${props => props.isMobileViewActive ? '60px' : '100px'};
	overflow-y: auto;
	margin-bottom: 8px;
`;

const Title = styled.div`
	font-size: 14px;
	margin-bottom: 8px;
	flex: 1;
	display: flex;
`;
const TitleText = styled.div`
	display: flex;
	margin: auto;
`;

const UnderlinedTitleText = styled.div`
	display: flex;
	margin: auto;
	text-decoration: underline;
	cursor: pointer;
`;

const InfoIconContainer = styled.div`
	display: flex;
	flex: 0;
`;

const InfoIcon = styled.i`
	margin: auto;
	position: relative; 
	font-size: 15px;
	color: #F89504;
	background-color: black;
	cursor: pointer;
	padding: 2px;
	text-shadow: none;
	:hover {
		color: #F89504;
	}
`;

const StyledLink = styled(Link)`
	color: white;
	margin: auto;
`;

//#endregion

export default function TrophyTooltip({
	trophyTooltip,
}: ITrophyTooltipProps) {

	const dispatch = useAppDispatch();

	const trophyTooltipContainerRef = useRef<HTMLDivElement>(null);

	const [isInfoVisible, setIsInfoVisible] = useState(false);

	const section = useSelector(
		(completeState: IStore): EnumSection => {
			return completeState.ui.section;
		});

	const isMobileViewActive = useSelector(
		(completeState: IStore): boolean => {
			return completeState.ui.isMobileViewActive;
		});

	const onClickOutside = useCallback(() => {
		dispatch(actions.setTrophyTooltip(null))
	}, [dispatch]);

	useClickOutside(trophyTooltipContainerRef, onClickOutside);

	const onInfoButtonClick = useCallback(() => {
		setIsInfoVisible(!isInfoVisible);
	}, [isInfoVisible]);

	const renderInfoButton = useCallback(() => {
		if (!trophyTooltip.description) {
			return null;
		}
		return (
			<InfoIconContainer title={'Information about competition'}>
				<InfoIcon
					className={'fa fa-solid fa-info'}
					onClick={onInfoButtonClick}
				/>
			</InfoIconContainer>
		);
	}, [trophyTooltip.description, onInfoButtonClick]);

	const renderDescription = useCallback(() => {
		if (!trophyTooltip.description || !isInfoVisible) {
			return null;
		}
		return (
			<DescriptionContainer
				isMobileViewActive={isMobileViewActive}
			>
				{trophyTooltip.description}
			</DescriptionContainer>
		);
	}, [trophyTooltip.description, isInfoVisible, isMobileViewActive]);

	const history = useHistory();

	const touchStartCapture = useCallback(() => {
		// TODO WARNING! this is a temporary fix. It seems to be a conflict with Map section and clickoutside touchstart event
		if (section === EnumSection.map && isMobileViewActive) {
			history.push(`/competitions?competition=${trophyTooltip.competitionName}`);
		}

	}, [trophyTooltip.competitionName, section, isMobileViewActive]);

	const competitionSectionClick = useCallback(() => {
		dispatch(actions.addCompetition(trophyTooltip.id));
	}, [trophyTooltip.competitionName, section, isMobileViewActive]);


	return (
		<TrophyTooltipContainer
			x={trophyTooltip.x}
			y={trophyTooltip.y}
			ref={trophyTooltipContainerRef}
		>
			<Title>
				{
					trophyTooltip.hasDetail
						? (
							section !== EnumSection.competitions
								? <StyledLink to={`/competitions?competition=${trophyTooltip.competitionName}`} onTouchStartCapture={touchStartCapture}>
									<TitleText>{trophyTooltip.title}</TitleText>
								</StyledLink>
								: <UnderlinedTitleText onClick={competitionSectionClick}>{trophyTooltip.title}</UnderlinedTitleText>
						)
						: <TitleText>{trophyTooltip.title}</TitleText>
				}
				
				{renderInfoButton()}
			</Title>
			{renderDescription()}
			{trophyTooltip.editions}
		</TrophyTooltipContainer>
	);

}

